import React from 'react';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="font-heading text-xl font-bold text-[#0b2545] mb-3 pl-4 border-l-[3px] border-[#c9a24b]">{title}</h2>
    <div className="text-slate-600 space-y-3 leading-relaxed">{children}</div>
  </div>
);

const PrivacyPolicy = () => (
  <main className="max-w-[860px] mx-auto px-4 lg:px-6 py-16">
    <h1 className="font-heading text-4xl font-bold text-[#0b2545] mb-3 tracking-tight">Privacy Policy</h1>
    <span className="block h-[2px] w-16 rounded-full mb-3" style={{ background: 'linear-gradient(90deg,#c9a24b,#e8c887)' }} />
    <p className="text-slate-500 mb-10">Last updated: July 2026</p>

    <Section title="1. Introduction">
      <p>
        Finirio Financial Services ("Finirio", "we", "us", or "our") is committed to protecting your
        personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard
        your information when you visit our website <strong>finirio.com</strong> or use our services.
      </p>
      <p>
        By accessing our website or submitting your information to us, you consent to the practices
        described in this policy. If you do not agree, please discontinue use of our services.
      </p>
    </Section>

    <Section title="2. Information We Collect">
      <p>We collect information you provide directly to us, including:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Name, email address, phone number, and date of birth</li>
        <li>PAN card number and Aadhaar number (where required for loan processing)</li>
        <li>Employment type, monthly income, and existing obligations</li>
        <li>Property details and loan requirements</li>
        <li>Bank account details (for disbursement purposes only)</li>
      </ul>
      <p>We also automatically collect certain technical data such as IP address, browser type, pages visited, and time spent on our website through cookies and similar technologies.</p>
    </Section>

    <Section title="3. How We Use Your Information">
      <p>We use the information we collect to:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Process your loan application and check eligibility</li>
        <li>Submit your application to partner banks and NBFCs on your behalf</li>
        <li>Contact you regarding your application status or offers</li>
        <li>Send relevant financial product updates (with your consent)</li>
        <li>Improve our website, products, and customer service</li>
        <li>Comply with legal and regulatory obligations under RBI guidelines</li>
        <li>Detect and prevent fraud or unauthorised access</li>
      </ul>
    </Section>

    <Section title="4. Sharing of Information">
      <p>
        We do not sell your personal data. We may share your information with:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Partner Banks and NBFCs:</strong> To process your loan application (e.g., HDFC Bank, SBI, ICICI Bank, Axis Bank, Kotak Mahindra, and others in our network).</li>
        <li><strong>Credit Bureaus:</strong> CIBIL, Experian, or CRIF for credit assessment purposes.</li>
        <li><strong>Service Providers:</strong> Technology and analytics vendors who assist in operating our platform, under strict confidentiality agreements.</li>
        <li><strong>Legal Authorities:</strong> Where required by law, court order, or regulatory directive.</li>
      </ul>
    </Section>

    <Section title="5. Telecom Communication Consent & DND Policy">
      <p>
        By submitting your mobile number and ticking the DND consent checkbox on our application form,
        you explicitly authorise Finirio Financial Services and its authorised representatives to contact
        you via <strong>calls, SMS, WhatsApp, and email</strong> in relation to your loan enquiry,
        application status, and related financial products — <strong>even if your mobile number is
        registered on the Do Not Disturb (DND) / National Do Not Call (NDNC) registry</strong>.
      </p>
      <p>
        This consent is granted in accordance with the <strong>Telecom Commercial Communications Customer
        Preference Regulations, 2018 (TCCCPR 2018)</strong> issued by the Telecom Regulatory Authority
        of India (TRAI) and the relevant directions of the Department of Telecommunications (DoT),
        Government of India. Under these regulations, a customer who has explicitly consented to receive
        communications from a specific entity for a specific purpose is exempt from DND restrictions for
        that purpose.
      </p>
      <p>
        Your consent covers:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Transactional messages — OTP, application updates, loan disbursal alerts</li>
        <li>Service messages — document reminders, appointment confirmations, EMI reminders</li>
        <li>Promotional messages — new loan offers, interest rate changes, financial tips (only where separately consented)</li>
      </ul>
      <p>
        You may withdraw this consent at any time by writing to us at{' '}
        <a href="mailto:sales@finirio.com" className="text-[#1e73be] hover:underline">sales@finirio.com</a>.
        Withdrawal of consent will not affect the lawfulness of communications made before the withdrawal.
        Please note that withdrawal may limit our ability to service your loan application.
      </p>
    </Section>

    <Section title="6. Data Security">
      <p>
        We implement industry-standard security measures including SSL encryption, secure server infrastructure,
        and access controls to protect your personal data against unauthorised access, alteration, disclosure,
        or destruction.
      </p>
      <p>
        While we take reasonable precautions, no method of transmission over the internet is 100% secure.
        We encourage you to use strong passwords and notify us immediately if you suspect any unauthorised use.
      </p>
    </Section>

    <Section title="7. Cookies">
      <p>
        Our website uses cookies to enhance your browsing experience. Cookies help us remember your preferences,
        analyse traffic, and serve relevant content. You may configure your browser to refuse cookies, though
        some features of the site may not function correctly as a result.
      </p>
    </Section>

    <Section title="8. Data Retention">
      <p>
        We retain your personal information for as long as necessary to fulfil the purposes for which it was
        collected, including legal, accounting, or reporting requirements. Loan-related records are typically
        retained for 7 years as required by financial regulations in India.
      </p>
    </Section>

    <Section title="9. Your Rights">
      <p>You have the right to:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Access the personal information we hold about you</li>
        <li>Request correction of inaccurate or incomplete data</li>
        <li>Request deletion of your data (subject to legal obligations)</li>
        <li>Withdraw consent for marketing communications at any time</li>
        <li>Lodge a complaint with the relevant data protection authority</li>
      </ul>
      <p>
        To exercise any of these rights, please contact us at{' '}
        <a href="mailto:sales@finirio.com" className="text-[#1e73be] hover:underline">sales@finirio.com</a>.
      </p>
    </Section>

    <Section title="10. Grievance Officer">
      <p>
        In accordance with the Information Technology Act, 2000 and the Information Technology
        (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, the name and contact
        details of the Grievance Officer are provided below. Any complaints or concerns regarding
        the processing of your personal data may be directed to:
      </p>
      <div className="rounded-xl p-5 mt-2 text-sm space-y-1 border border-[#c9a24b]/30" style={{ backgroundColor: '#FBF9F4' }}>
        <p className="font-semibold text-slate-800">Grievance Officer — Finirio Financial Services</p>
        <p>Email: <a href="mailto:sales@finirio.com" className="text-[#1e73be] hover:underline">sales@finirio.com</a></p>
        <p>Response time: Within 30 days of receipt of complaint</p>
      </div>
    </Section>

    <Section title="11. Third-Party Links">
      <p>
        Our website may contain links to third-party websites. We are not responsible for the privacy practices
        of those sites and encourage you to review their respective privacy policies.
      </p>
    </Section>

    <Section title="12. Children's Privacy">
      <p>
        Our services are not directed to individuals under the age of 18. We do not knowingly collect personal
        information from minors. If we become aware that a minor has submitted information, we will delete it promptly.
      </p>
    </Section>

    <Section title="13. Changes to This Policy">
      <p>
        We may update this Privacy Policy from time to time. We will notify you of material changes by posting the
        updated policy on our website with a revised "Last updated" date. Continued use of our services after any
        changes constitutes your acceptance of the updated policy.
      </p>
    </Section>

    <Section title="14. Contact Us">
      <p>If you have questions or concerns about this Privacy Policy or your data, please contact:</p>
      <div className="rounded-xl p-5 mt-2 text-sm space-y-1 border border-[#c9a24b]/30" style={{ backgroundColor: '#FBF9F4' }}>
        <p className="font-semibold text-slate-800">Finirio Financial Services</p>
        <p>Email: <a href="mailto:sales@finirio.com" className="text-[#1e73be] hover:underline">sales@finirio.com</a></p>
        <p>Website: <a href="https://finirio.com" className="text-[#1e73be] hover:underline">www.finirio.com</a></p>
      </div>
    </Section>
  </main>
);

export default PrivacyPolicy;
